// Visible toasts for errors and notices — replaces console-only catches.
// Usage:
//   import { notifyError, notify } from '../notify';
//   catch (err) { notifyError(err, 'Could not save'); }
//
// Toasts are plain DOM (top-right, dark theme) so any module can raise one
// without prop drilling. Identical messages within a few seconds are merged.

const CONTAINER_ID = 'rm-toast-container';
const recent = new Map(); // message -> timestamp (dedupe bursts)

function getContainer() {
  let el = document.getElementById(CONTAINER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = CONTAINER_ID;
    Object.assign(el.style, {
      position: 'fixed', top: '16px', right: '16px', zIndex: '10000',
      display: 'flex', flexDirection: 'column', gap: '8px',
      maxWidth: 'min(420px, calc(100vw - 32px))', pointerEvents: 'none'
    });
    document.body.appendChild(el);
  }
  return el;
}

// Best human-readable message from an axios/fetch error (or a string).
export function errorMessage(err, fallback) {
  if (!err) return fallback || 'Something went wrong';
  if (typeof err === 'string') return err;
  const d = err.response && err.response.data;
  let m = null;
  if (d) {
    if (typeof d === 'string') m = d.trim().startsWith('<') ? null : d;
    else m = (d.error && (d.error.message || d.error)) || d.message || null;
  }
  if (m && typeof m !== 'string') {
    try { m = JSON.stringify(m); } catch (e) { m = null; }
  }
  if (!m) m = err.message || fallback || 'Something went wrong';
  if (m.length > 300) m = m.slice(0, 300) + '…';
  return m;
}

export function notify(message, type = 'info', ms = 6000) {
  if (!message || typeof document === 'undefined') return;
  const now = Date.now();
  if (recent.has(message) && now - recent.get(message) < 3000) return;
  recent.set(message, now);

  const colors = {
    error: { border: '#ef4444', text: '#fca5a5' },
    success: { border: '#22c55e', text: '#86efac' },
    info: { border: '#ff4500', text: '#ffffff' }
  }[type] || { border: '#ff4500', text: '#ffffff' };

  const toast = document.createElement('div');
  toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
  toast.title = 'Click to dismiss';
  Object.assign(toast.style, {
    background: '#1a1a1a', border: '1px solid ' + colors.border, borderLeftWidth: '4px',
    borderRadius: '8px', padding: '10px 14px', color: colors.text,
    fontSize: '13px', lineHeight: '1.4', boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    cursor: 'pointer', pointerEvents: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    animation: 'fadeIn 0.2s ease'
  });
  toast.textContent = message;
  const remove = () => { if (toast.parentNode) toast.parentNode.removeChild(toast); };
  toast.addEventListener('click', remove);
  getContainer().appendChild(toast);
  setTimeout(remove, ms);
}

// Show an error toast with the server's message. 401s are skipped: the
// global interceptor in App.js already sends the user back to login.
export function notifyError(err, context) {
  if (err && err.response && err.response.status === 401) return;
  // keep the full error in the console for debugging
  // eslint-disable-next-line no-console
  console.error(context || 'Error', err);
  const msg = errorMessage(err);
  notify(context ? `${context}: ${msg}` : msg, 'error', 8000);
}
